'use client'

import React, { useRef, useMemo, useState, useEffect } from 'react'
import { Navigation, A11y, Autoplay } from 'swiper/modules'
import { Swiper } from 'swiper/react'

import ASwiperNavigation from './components/a-swiper-navigation'

import type { Swiper as SwiperType } from 'swiper'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/autoplay'
import './css/z-swiper.css'

export interface ZSwiperProps {
	children: React.ReactNode
	spaceBetween?: number
	initialSlidesPerView?: number
	centeredSlides?: boolean
	showCounter?: boolean
	showProgressbar?: boolean
	navigationClass?: string
	loop?: boolean
	slidesPerView?: number | 'auto'
	slidesPerGroup?: number
	autoplay?: boolean
	autoplayDelay?: number
	breakpoints?: {
		[key: number]: {
			slidesPerView: any
			slidesPerGroup?: number
			spaceBetween?: number
		}
	}
	className?: string
	showArrows?: boolean
	navigationPosition?: 'top' | 'bottom'
	title?: { show: boolean; text: string }
}

const ZSwiper: React.FC<ZSwiperProps> = ({
	children,
	spaceBetween = 16,
	initialSlidesPerView = 1.1,
	centeredSlides = false,
	loop = false,
	showCounter = true,
	showProgressbar = true,
	navigationClass = '',
	slidesPerGroup = 1,
	breakpoints = {},
	className = '',
	showArrows = true,
	autoplay = false,
	autoplayDelay = 3000,
	navigationPosition = 'bottom',
	title = { show: false, text: '' }
}) => {
	const [mounted, setMounted] = useState(false)
	const swiperRef = useRef<SwiperType | null>(null)

	useEffect(() => {
		setMounted(true)
	}, [])

	const swiperConfig = useMemo(() => {
		const config: any = {
			centeredSlides,
			spaceBetween,
			loop,
			slidesPerGroup,
			slidesPerView: initialSlidesPerView,
			watchSlidesProgress: true,
			observer: true,
			observeParents: true,
			autoplay: autoplay
				? {
						delay: autoplayDelay,
						disableOnInteraction: false,
						pauseOnMouseEnter: true
				  }
				: false
		}

		if (Object.keys(breakpoints).length > 0) {
			config.breakpoints = breakpoints
		}

		return config
	}, [
		spaceBetween,
		centeredSlides,
		loop,
		slidesPerGroup,
		breakpoints,
		initialSlidesPerView,
		autoplay,
		autoplayDelay
	])

	if (!mounted) {
		return <div className={`swiper-universal-cont ${className}`}>{/* Loading skeleton */}</div>
	}

	return (
		<div className={`swiper-universal-cont ${className}`}>
			{navigationPosition === 'top' && (
				<ASwiperNavigation
					swiperRef={swiperRef}
					showArrows={showArrows}
					title={title}
					showCounter={showCounter}
					showProgressbar={showProgressbar}
					navigationClass={navigationClass}
				/>
			)}

			<Swiper
				modules={[Navigation, A11y, Autoplay]}
				{...swiperConfig}
				className='swiper-container'
				onSwiper={swiper => {
					swiperRef.current = swiper
				}}
			>
				{children}
			</Swiper>

			{navigationPosition === 'bottom' && (
				<ASwiperNavigation
					swiperRef={swiperRef}
					showArrows={showArrows}
					title={title}
					showCounter={showCounter}
					showProgressbar={showProgressbar}
					navigationClass={navigationClass}
				/>
			)}
		</div>
	)
}

export default ZSwiper
